'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceCardProps {
  service: {
    id: string;
    title: string;
    description: string;
    features: string[];
    image: string;
    alt: string;
    icon: string;
    priceRange: string;
    timeline: string;
    scope: string;
  };
  onLearnMore: (serviceId: string) => void;
}

const ServiceCard = ({ service, onLearnMore }: ServiceCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:border-zinc-300 hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={service.image}
          alt={service.alt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-[#1B5E4F] rounded-xl flex items-center justify-center">
              <Icon name={service.icon as any} size={24} className="text-white" />
            </div>
            <h3 className="font-headline text-2xl font-normal text-white">{service.title}</h3>
          </div>
        </div>
      </div>

      <div className="p-6">
        <p className="text-black/70 font-normal mb-4 line-clamp-2">
          {service.description}
        </p>

        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-black font-medium">Starting From:</span>
            <span className="text-[#1B5E4F] font-medium">{service.priceRange}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-black font-medium">Timeline:</span>
            <span className="text-black/60 font-normal">{service.timeline}</span>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-4 py-2 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors duration-200 mb-4"
        >
          <span className="text-sm font-medium text-black">Key Features</span>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className={`text-[#1B5E4F] transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isExpanded && (
          <ul className="space-y-2 mb-4">
            {service.features.map((feature, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={20} className="text-[#1B5E4F] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-black/70 font-normal">{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={() => onLearnMore(service.id)}
          className="w-full px-6 py-3 bg-[#1B5E4F] text-white font-medium rounded-full hover:bg-[#1B5E4F]/90 transition-all duration-200 active:scale-95 min-h-[44px]"
        >
          Learn More & Book Consultation
        </button>
      </div>
    </div>
  );
};

export default ServiceCard;